/**
 * WhatsApp-style message status indicator
 * Shows check marks for sent/delivered/read status
 * Matches mobile MessageStatusIndicator.tsx
 */
import React from 'react';
import './MessageStatusIndicator.css';

/**
 * Message delivery status (matches mobile app)
 * @typedef {'sending' | 'sent' | 'delivered' | 'read' | 'failed'} MessageStatus
 */

/**
 * Displays WhatsApp-style check marks:
 * - Clock icon: sending to server
 * - Single gray check: sent to server
 * - Double gray checks: delivered to recipient
 * - Double blue checks: read by recipient
 *
 * @param {Object} props
 * @param {MessageStatus} props.status - Message delivery status
 * @param {number} [props.size=14] - Size of the icons
 * @param {string} [props.color] - Custom color override
 */
const MessageStatusIndicator = ({ status, size = 14, color }) => {
  const getColor = () => {
    if (color) return color;
    // Read = light cyan (#B3F5FF) for high contrast against the purple outgoing bubble — the
    // old #5B47D6 was the same hue as the bubble, so the read ticks were nearly invisible.
    // Others = translucent white (sending/sent/delivered).
    return status === 'read' ? '#B3F5FF' : 'rgba(255, 255, 255, 0.7)';
  };

  const checkColor = getColor();
  const iconSize = `${size}px`;

  if (status === 'sending') {
    // Clock icon for sending
    return (
      <div className="msg-status-indicator">
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke={checkColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </div>
    );
  }

  if (status === 'sent') {
    // Single check mark
    return (
      <div className="msg-status-indicator">
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke={checkColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    );
  }

  // Double check marks for delivered/read
  return (
    <div className="msg-status-indicator">
      <div className="msg-status-double-check">
        <svg
          className="msg-status-first-check"
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke={checkColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg
          className="msg-status-second-check"
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke={checkColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </div>
  );
};

export default MessageStatusIndicator;
